import type { AnimationItem } from "lottie-web";
import { THREE } from "@scrolly/renderer";
import { logError } from "../utils/logger";
import type { LottieContent } from "@scrolly/post-types";

const MAX_CANVAS_SIZE = 1024;

// Lazy-load lottie-web only when a Lottie layer is actually instantiated.
// Without a Lottie layer, this ~95 KB gzipped bundle is never fetched.
type LottieModule = typeof import("lottie-web/build/player/lottie_light_canvas");
let lottieModulePromise: Promise<LottieModule> | null = null;
function loadLottie(): Promise<LottieModule> {
    if (!lottieModulePromise) {
        lottieModulePromise = import("lottie-web/build/player/lottie_light_canvas");
    }
    return lottieModulePromise;
}

class LottiePlayer {
    canvas: HTMLCanvasElement;
    texture: THREE.CanvasTexture | undefined;
    animation: AnimationItem | undefined;
    isPlaying = false;
    totalFrames = 0;

    constructor() {
        this.generateTexture = this.generateTexture.bind(this);
        this.seek = this.seek.bind(this);

        // Use a hidden regular canvas (OffscreenCanvas not supported by lottie-web)
        const canvas = document.createElement("canvas");
        canvas.style.display = "none";
        this.canvas = canvas;
    }

    async generateTexture(props: LottieContent, url: string): Promise<{
        material: THREE.MeshBasicMaterial;
        texture: THREE.CanvasTexture;
        height: number;
        width: number;
    }> {
        try {
            const res = await fetch(url);
            const animationData = await res.json();

            // Determine canvas size from animation data, capped for performance
            const animW = animationData.w || props.width || 512;
            const animH = animationData.h || props.height || 512;
            const scale = Math.min(1, MAX_CANVAS_SIZE / Math.max(animW, animH));
            const canvasW = Math.round(animW * scale);
            const canvasH = Math.round(animH * scale);

            this.canvas.width = canvasW;
            this.canvas.height = canvasH;

            // Container div required by lottie-web (it appends the canvas internally,
            // but with rendererSettings.context we reuse our own canvas)
            const container = document.createElement("div");
            container.style.display = "none";
            container.style.width = `${canvasW}px`;
            container.style.height = `${canvasH}px`;
            document.body.appendChild(container);

            const ctx = this.canvas.getContext("2d");
            if (!ctx) {
                logError(new Error("[Lottie] Could not get 2d context"));
                return this.buildFallbackResult(props);
            }

            const lottieModule = await loadLottie();
            const lottie = lottieModule.default;

            const anim = lottie.loadAnimation({
                container,
                renderer: "canvas",
                loop: props.loop ?? true,
                autoplay: false,
                animationData,
                rendererSettings: {
                    context: ctx,
                    clearCanvas: true,
                    preserveAspectRatio: "xMidYMid meet",
                },
            });

            this.animation = anim;
            this.totalFrames = anim.totalFrames;

            const canvasTexture = new THREE.CanvasTexture(this.canvas);
            this.texture = canvasTexture;
            canvasTexture.minFilter = THREE.LinearFilter;
            canvasTexture.magFilter = THREE.LinearFilter;
            canvasTexture.format = THREE.RGBAFormat;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const threeAny: any = THREE;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (canvasTexture as any).colorSpace = threeAny.SRGBColorSpace;

            const material = new THREE.MeshBasicMaterial({
                map: canvasTexture,
                alphaTest: 0.01,
                transparent: true,
                depthWrite: false,
                side: props.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
            });

            // Render the first frame so the texture is not blank initially
            anim.goToAndStop(0, true);

            return {
                material,
                texture: canvasTexture,
                height: animH,
                width: animW,
            };
        } catch (err) {
            logError(err as Error);
            return this.buildFallbackResult(props);
        }
    }

    private buildFallbackResult(props: LottieContent) {
        this.canvas.width = 2;
        this.canvas.height = 2;
        const canvasTexture = new THREE.CanvasTexture(this.canvas);
        this.texture = canvasTexture;
        const material = new THREE.MeshBasicMaterial({
            map: canvasTexture,
            transparent: true,
            depthWrite: false,
            side: props.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
        });
        return {
            material,
            texture: canvasTexture,
            height: props.height || 512,
            width: props.width || 512,
        };
    }

    play() {
        if (this.animation && !this.isPlaying) {
            this.isPlaying = true;
            this.animation.play();
        }
    }

    pause() {
        if (this.animation && this.isPlaying) {
            this.animation.pause();
            this.isPlaying = false;
        }
    }

    /**
     * Seek to a specific frame number and stop.
     */
    seek(frame: number) {
        if (this.animation) {
            this.animation.goToAndStop(frame, true);
            this.isPlaying = false;
        }
    }

    /**
     * Seek to a progress value between 0 and 1.
     */
    seekToProgress(progress: number) {
        if (this.animation) {
            const frame = Math.round(progress * (this.totalFrames - 1));
            this.animation.goToAndStop(frame, true);
            this.isPlaying = false;
        }
    }

    delete() {
        if (this.animation) {
            this.animation.destroy();
            this.animation = undefined;
        }
        if (this.texture) {
            this.texture.dispose();
            this.texture = undefined;
        }
        this.canvas.remove();
    }
}

export default LottiePlayer;
